/* ==========================================================================
   Footer Component (Official Logo, ISO Pictograms, EUIPO Trademark & Award Stamps)
   100% Multi-Language i18n, SVG Icons, Company Column & Cookie Settings Trigger
   ========================================================================== */

import { t, getLocale } from '../locales/i18n.js';
import { openCookieSettings } from './CookieBanner.js';
import { getSvgIcon } from './Icons.js';

const footerTranslations = {
  en: {
    brandDesc: 'SED makes the world safer, at the speed of sound. With SED you can address threats quickly.',
    iso9001Sub: 'Quality System',
    iso27001Sub: 'Info Security',
    euTrademarkSub: 'Reg. No. 018598475',
    aiAwardsTitle: 'AI Awards',
    aiAwardsYear: '2023 ↗',
    aiAwardsSub: 'Winner in Public Administration',
    vizionariTitle: 'Visionaries',
    vizionariYear: '2023 ↗',
    vizionariSub: 'Honorable Jury Mention',
    czech100Num: '100',
    czech100Title: 'Czech 100 Best',
    czech100Year: '2025 · IT & Engineering ↗',
    cookieSettings: 'Cookie Settings',
    madeIn: 'Made with ❤️ in Pilsen, Czech Republic'
  },
  cs: {
    brandDesc: 'SED dělá svět bezpečnějším, rychlostí zvuku. S technologií SED můžete bleskově reagovat na bezpečnostní hrozby.',
    iso9001Sub: 'Systém jakosti',
    iso27001Sub: 'Bezpečnost informací',
    euTrademarkSub: 'Reg. č. 018598475',
    aiAwardsTitle: 'AI Awards',
    aiAwardsYear: '2023 ↗',
    aiAwardsSub: 'Vítěz v kategorii Veřejná správa',
    vizionariTitle: 'Vizionáři',
    vizionariYear: '2023 ↗',
    vizionariSub: 'Čestné uznání odborné poroty',
    czech100Num: '100',
    czech100Title: 'Českých 100 Nejlepších',
    czech100Year: '2025 · IT & Vývoj ↗',
    cookieSettings: 'Nastavení cookies',
    madeIn: 'Vyvinuto s ❤️ v Plzni, Česká republika'
  },
  de: {
    brandDesc: 'SED macht die Welt sicherer mit Schallgeschwindigkeit. Mit SED reagieren Sie blitzschnell auf Bedrohungen.',
    iso9001Sub: 'Qualitätsmanagement',
    iso27001Sub: 'Informationssicherheit',
    euTrademarkSub: 'Reg.-Nr. 018598475',
    aiAwardsTitle: 'AI Awards',
    aiAwardsYear: '2023 ↗',
    aiAwardsSub: 'Gewinner in Öffentliche Verwaltung',
    vizionariTitle: 'Visionäre',
    vizionariYear: '2023 ↗',
    vizionariSub: 'Ehrenpreis der Fachjury',
    czech100Num: '100',
    czech100Title: 'Tschechische Top 100',
    czech100Year: '2025 · IT & Entwicklung ↗',
    cookieSettings: 'Cookie-Einstellungen',
    madeIn: 'Entwickelt mit ❤️ in Pilsen, Tschechische Republik'
  },
  es: {
    brandDesc: 'SED hace el mundo más seguro a la velocidad del sonido. Con SED puede abordar las amenazas rápidamente.',
    iso9001Sub: 'Gestión de Calidad',
    iso27001Sub: 'Seguridad de Información',
    euTrademarkSub: 'Reg. Nº 018598475',
    aiAwardsTitle: 'AI Awards',
    aiAwardsYear: '2023 ↗',
    aiAwardsSub: 'Ganador en Administración Pública',
    vizionariTitle: 'Visionarios',
    vizionariYear: '2023 ↗',
    vizionariSub: 'Mención de honor del jurado',
    czech100Num: '100',
    czech100Title: '100 Mejores Checas',
    czech100Year: '2025 · TI e Ingeniería ↗',
    cookieSettings: 'Configuración de cookies',
    madeIn: 'Desarrollado con ❤️ en Pilsen, República Checa'
  },
  ar: {
    brandDesc: 'SED يجعل العالم أكثر أماناً بسرعة الصوت. مع SED يمكنك التعامل مع التهديدات بسرعة.',
    iso9001Sub: 'نظام إدارة الجودة',
    iso27001Sub: 'أمن المعلومات',
    euTrademarkSub: 'رقم 018598475',
    aiAwardsTitle: 'جوائز الذكاء الاصطناعي',
    aiAwardsYear: '2023 ↗',
    aiAwardsSub: 'الفائز في فئة الإدارة العامة',
    vizionariTitle: 'أصحاب الرؤى',
    vizionariYear: '2023 ↗',
    vizionariSub: 'تقدير شرفي من لجنة التحكيم',
    czech100Num: '100',
    czech100Title: 'أفضل 100 شركة تشيكية',
    czech100Year: '2025 · تكنولوجيا وهندسة ↗',
    cookieSettings: 'إعدادات ملفات تعريف الارتباط',
    madeIn: 'صنع بـ ❤️ في بلزن، جمهورية التشيك'
  },
  pt: {
    brandDesc: 'SED torna o mundo mais seguro à velocidade do som. Com o SED você pode enfrentar ameaças rapidamente.',
    iso9001Sub: 'Gestão da Qualidade',
    iso27001Sub: 'Segurança da Informação',
    euTrademarkSub: 'Reg. Nº 018598475',
    aiAwardsTitle: 'AI Awards',
    aiAwardsYear: '2023 ↗',
    aiAwardsSub: 'Vencedor em Administração Pública',
    vizionariTitle: 'Visionários',
    vizionariYear: '2023 ↗',
    vizionariSub: 'Menção honrosa do júri especializado',
    czech100Num: '100',
    czech100Title: '100 Melhores Checas',
    czech100Year: '2025 · TI e Engenharia ↗',
    cookieSettings: 'Configurações de cookies',
    madeIn: 'Desenvolvido com ❤️ em Plzeň, República Checa'
  },
  it: {
    brandDesc: 'SED rende il mondo più sicuro alla velocità del suono. Con la tecnologia SED puoi reagire tempestivamente a ogni minaccia.',
    iso9001Sub: 'Sistema di Qualità',
    iso27001Sub: 'Sicurezza delle Informazioni',
    euTrademarkSub: 'Reg. n. 018598475',
    aiAwardsTitle: 'AI Awards',
    aiAwardsYear: '2023 ↗',
    aiAwardsSub: 'Vincitore nella Pubblica Amministrazione',
    vizionariTitle: 'Visionari',
    vizionariYear: '2023 ↗',
    vizionariSub: 'Menzione d\'onore della giuria tecnica',
    czech100Num: '100',
    czech100Title: 'Top 100 Imprese Ceche',
    czech100Year: '2025 · IT e Ingegneria ↗',
    cookieSettings: 'Impostazioni Cookie',
    madeIn: 'Sviluppato con ❤️ a Plzeň, Repubblica Ceca'
  }
};

export function renderFooter(onNavigate) {
  const footer = document.createElement('footer');
  footer.className = 'sed-footer';

  const lang = getLocale() || 'en';
  const f = footerTranslations[lang] || footerTranslations.en;

  footer.innerHTML = `
    <div class="sed-container">
      <div class="sed-footer-grid">
        <!-- Brand Info, Social Links, ISO Badges & Award Stamps -->
        <div>
          <div class="sed-brand" style="margin-bottom: 1.25rem;">
            <svg width="42" height="36" viewBox="-362 -98 600 500" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFFFFF" d="M195.627,247.888c-12.865-13.056-29.891-19.539-51.041-19.539H93.608V390.41h50.978c21.15,0,38.176-6.452,51.041-19.402c12.875-12.933,19.326-29.783,19.326-50.477v-22.168C214.953,277.719,208.502,260.884,195.627,247.888z M193.028,320.531c0,15.438-4.349,28.074-12.978,37.952c-8.666,9.868-20.469,14.804-35.465,14.804h-29.037V245.592h29.037c14.996,0,26.799,4.951,35.465,14.819c8.629,9.869,12.978,22.466,12.978,37.719V320.531z"/>
              <polygon fill="#FFFFFF" points="-12.496,315.309 54.06,315.309 54.06,298.01 -12.496,298.01 -12.496,245.592 64.189,245.592 64.189,228.349 -34.437,228.349 -34.437,340.438 -34.437,347.964 -34.437,373.295 -34.437,373.471 -34.437,390.521 -23.091,390.521 65.401,390.41 65.401,373.287 -12.496,373.287"/>
              <path fill="#FFFFFF" d="M73.858,140.234c-6.556,8.528-14.932,12.757-25.146,12.757c-12.308,0-22.089-3.529-29.375-10.646C12.061,135.261,8.39,124.3,8.39,109.44h-54.477l-0.376,1.139c-0.642,27.77,7.679,48.952,24.953,63.531c17.271,14.587,40.66,21.871,70.223,21.871c26.582,0,48.382-8.191,65.295-24.56c16.966-16.384,25.427-38.368,25.427-66.002V-37.793v-49.601v-0.594h-483.164l-0.205,478.51h192.67h2.78h49.032c6.993-2.15,13.239-5.184,18.599-9.259c11.354-8.634,17.026-20.028,17.026-34.181c0-4.822-0.585-9.252-1.749-13.303c-1.172-4.046-2.548-7.493-4.133-10.383c-1.589-2.872-3.883-5.632-6.884-8.281c-3.009-2.646-5.725-4.741-8.144-6.298c-2.419-1.557-5.721-3.186-9.894-4.902c-4.172-1.709-7.554-2.952-10.142-3.731c-2.591-0.778-6.138-1.869-10.643-3.266c-13.523-4.204-23.128-8.167-28.8-11.898c-5.681-3.738-8.513-9.571-8.513-17.5c0-7.471,2.788-13.342,8.385-17.619c5.592-4.269,13.398-6.419,23.42-6.419c6.511,0,13.311,1.252,20.408,3.747c7.097,2.479,13.066,5.745,17.913,9.789l11.763-15.63c-6.005-4.983-13.769-9.027-23.284-12.14c-9.52-3.106-19.036-4.662-28.557-4.662c-16.191,0-29.297,4.165-39.316,12.485c-10.024,8.321-15.027,19.016-15.027,32.078c0,6.995,1.292,13.069,3.884,18.197c2.583,5.143,6.51,9.419,11.774,12.837c5.251,3.418,10.137,6.066,14.647,7.928c4.509,1.869,10.431,3.972,17.78,6.307c6.511,2.03,11.145,3.498,13.896,4.43c2.76,0.931,6.303,2.413,10.647,4.437c4.34,2.021,7.39,4.044,9.139,6.064c1.757,2.022,3.338,4.663,4.758,7.93c1.42,3.271,2.046,7.091,1.881,11.433c-0.34,8.096-3.842,14.507-10.521,19.248c-6.676,4.749-16.023,7.117-28.046,7.117c-7.848,0-15.987-1.708-24.42-5.128c-6.266-2.543-11.674-5.647-16.235-9.315h-1.97h-5.416h-2.56h-70.547c-0.189-0.039-0.31-0.055-0.494-0.079c-59.062,0-57.449-56.863-57.449-56.863l-0.128-20.179l0.421-276.999c0,0,0-0.208,0-0.352V5.799c0.041-6.499,2.236-56.141,57.445-56.141H25.416L83.631,6.513v98.907C83.631,120.127,80.357,131.737,73.858,140.234z"/>
            </svg>
            <div>
              <div class="sed-brand-title">Sound Event Detector<sup>®</sup></div>
              <div class="sed-brand-tagline">JALUD Embedded s.r.o.</div>
            </div>
          </div>

          <p style="font-size: 0.9rem; line-height: 1.65; color: rgba(255,255,255,0.9); margin-bottom: 1.25rem; max-width: 380px;">
            ${f.brandDesc}
          </p>

          <!-- Social Media Channels & ISO / Trademark Badges in One Compact Row -->
          <div style="display: flex; gap: 6px; align-items: center; flex-wrap: nowrap; margin-bottom: 1.25rem;">
            <!-- Social Icons Group -->
            <div style="display: flex; gap: 8px; align-items: center; flex-shrink: 0;">
              <a href="https://www.facebook.com/jaludembedded" target="_blank" rel="noopener noreferrer" aria-label="Facebook" style="color: #ffffff; font-size: 1.15rem; text-decoration: none; opacity: 0.9; transition: var(--transition-fast);">f</a>
              <a href="https://x.com/jaludembedded" target="_blank" rel="noopener noreferrer" aria-label="Twitter X" style="color: #ffffff; font-size: 1.15rem; text-decoration: none; opacity: 0.9; transition: var(--transition-fast);">𝕏</a>
              <a href="https://www.linkedin.com/company/jalud-embedded/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" style="color: #ffffff; font-size: 1.15rem; font-weight: 800; text-decoration: none; opacity: 0.9; transition: var(--transition-fast);">in</a>
              <a href="https://www.youtube.com/@jaludembedded" target="_blank" rel="noopener noreferrer" aria-label="YouTube" style="color: #ffffff; font-size: 1.15rem; text-decoration: none; opacity: 0.9; transition: var(--transition-fast);">▶</a>
            </div>

            <!-- Separator Line -->
            <div style="width: 1px; height: 24px; background: rgba(255,255,255,0.25); margin: 0 1px; flex-shrink: 0;"></div>

            <!-- ISO 9001 Pictogram -->
            <div style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.2); backdrop-filter: blur(8px); border-radius: 6px; padding: 0.25rem 0.45rem; display: flex; align-items: center; gap: 5px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); flex-shrink: 0;">
              <div style="width: 20px; height: 20px; background: var(--blue-primary); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #ffffff; font-weight: 900; font-size: 0.58rem; flex-shrink: 0;">ISO</div>
              <div style="white-space: nowrap;">
                <div style="font-weight: 800; font-size: 0.7rem; color: #ffffff; line-height: 1.1;">ISO 9001</div>
                <div style="font-size: 0.54rem; color: rgba(255,255,255,0.75); font-weight: 600;">${f.iso9001Sub}</div>
              </div>
            </div>

            <!-- ISO 27001 Pictogram -->
            <div style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.2); backdrop-filter: blur(8px); border-radius: 6px; padding: 0.25rem 0.45rem; display: flex; align-items: center; gap: 5px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); flex-shrink: 0;">
              <div style="width: 20px; height: 20px; background: #10b981; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #ffffff; font-weight: 900; font-size: 0.58rem; flex-shrink: 0;">ISO</div>
              <div style="white-space: nowrap;">
                <div style="font-weight: 800; font-size: 0.7rem; color: #ffffff; line-height: 1.1;">ISO 27001</div>
                <div style="font-size: 0.54rem; color: rgba(255,255,255,0.75); font-weight: 600;">${f.iso27001Sub}</div>
              </div>
            </div>

            <!-- EUIPO Registered Trademark Badge -->
            <div style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.2); backdrop-filter: blur(8px); border-radius: 6px; padding: 0.25rem 0.45rem; display: flex; align-items: center; gap: 5px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); flex-shrink: 0;">
              <div style="width: 20px; height: 20px; background: #f59e0b; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #000000; font-weight: 900; font-size: 0.72rem; flex-shrink: 0;">®</div>
              <div style="white-space: nowrap;">
                <div style="font-weight: 800; font-size: 0.7rem; color: #ffffff; line-height: 1.1;">EU Trademark</div>
                <div style="font-size: 0.54rem; color: rgba(255,255,255,0.75); font-weight: 600;">${f.euTrademarkSub}</div>
              </div>
            </div>
          </div>

          <!-- Official Award Badges / Stamps (Linked to About Us) -->
          <div style="display: flex; gap: 10px; align-items: stretch; flex-wrap: nowrap;">
            <!-- AI Awards Stamp -->
            <a href="#about-us" data-route="about-us" style="text-decoration: none; display: flex;" title="${f.aiAwardsTitle} - ${f.aiAwardsSub}">
              <div style="background-color: #ffffff; color: var(--navy-dark); border-radius: 12px; padding: 0.65rem 0.5rem; width: 114px; min-height: 90px; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); transition: transform 0.2s;" onmouseover="this.style.transform='translateY(-3px)'" onmouseout="this.style.transform='none'">
                <div style="font-family: var(--font-heading); font-weight: 900; font-size: 0.82rem; line-height: 1.15;">${f.aiAwardsTitle}</div>
                <div style="font-size: 0.68rem; font-weight: 800; color: #eab308; margin-top: 2px;">${f.aiAwardsYear}</div>
                <div style="font-size: 0.56rem; font-weight: 700; color: var(--text-muted); margin-top: 3px; line-height: 1.2;">${f.aiAwardsSub}</div>
              </div>
            </a>

            <!-- Vizionari Stamp -->
            <a href="#about-us" data-route="about-us" style="text-decoration: none; display: flex;" title="${f.vizionariTitle} - ${f.vizionariSub}">
              <div style="background-color: #ffffff; color: var(--navy-dark); border-radius: 12px; padding: 0.65rem 0.5rem; width: 114px; min-height: 90px; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); transition: transform 0.2s;" onmouseover="this.style.transform='translateY(-3px)'" onmouseout="this.style.transform='none'">
                <div style="font-family: var(--font-heading); font-weight: 900; font-size: 0.82rem; color: #1e3a8a; line-height: 1.15;">${f.vizionariTitle}</div>
                <div style="font-size: 0.68rem; font-weight: 800; color: #ef4444; margin-top: 2px;">${f.vizionariYear}</div>
                <div style="font-size: 0.56rem; font-weight: 700; color: var(--text-muted); margin-top: 3px; line-height: 1.2;">${f.vizionariSub}</div>
              </div>
            </a>

            <!-- Czech 100 Best Stamp -->
            <a href="#about-us" data-route="about-us" style="text-decoration: none; display: flex;" title="${f.czech100Title} - ${f.czech100Year}">
              <div style="background-color: #ffffff; color: var(--navy-dark); border-radius: 12px; padding: 0.65rem 0.5rem; width: 114px; min-height: 90px; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); transition: transform 0.2s;" onmouseover="this.style.transform='translateY(-3px)'" onmouseout="this.style.transform='none'">
                <div style="font-family: var(--font-heading); font-weight: 900; font-size: 0.82rem; color: #b45309; line-height: 1.15;">${f.czech100Num}</div>
                <div style="font-size: 0.56rem; font-weight: 800; color: var(--navy-dark); text-transform: uppercase; line-height: 1.2; margin-top: 1px;">${f.czech100Title}</div>
                <div style="font-size: 0.58rem; font-weight: 800; color: #b45309; margin-top: 2px; line-height: 1.15;">${f.czech100Year}</div>
              </div>
            </a>
          </div>
        </div>

        <!-- Navigation & Company Links (Renamed to Společnost / Company) -->
        <div>
          <h4 class="sed-footer-title">${t('footer.company') || 'Company'}</h4>
          <ul class="sed-footer-links">
            <li><a href="#about-us" data-route="about-us">${getSvgIcon('aboutUs', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.aboutUs')}</span></a></li>
            <li><a href="#news" data-route="news">${getSvgIcon('news', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.news')}</span></a></li>
            <li><a href="#case-studies" data-route="case-studies">${getSvgIcon('caseStudy', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.caseStudies')}</span></a></li>
            <li><a href="#product-updates" data-route="product-updates">${getSvgIcon('productUpdate', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.productUpdates') || 'Product Updates'}</span></a></li>
            <li><a href="#distributors-map" data-route="distributors-map">${getSvgIcon('coopMap', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.distributorsMap')}</span></a></li>
            <li><a href="#contact" data-route="contact">${getSvgIcon('contact', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.contact')}</span></a></li>
          </ul>
        </div>

        <!-- Legal & Compliance Links (Organized into 2 Balanced Columns) -->
        <div>
          <h4 class="sed-footer-title">${t('footer.legalHeading') || t('footer.legal')}</h4>
          <div class="sed-footer-legal-subgrid">
            <ul class="sed-footer-links">
              <li><a href="#eula" data-route="eula">${getSvgIcon('eula', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.eula')}</span></a></li>
              <li><a href="#terms-and-conditions" data-route="terms-and-conditions">${getSvgIcon('terms', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.terms')}</span></a></li>
              <li><a href="#support-and-maintenance" data-route="support-and-maintenance">${getSvgIcon('support', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.supportTerms')}</span></a></li>
              <li><a href="#code-of-ethics" data-route="code-of-ethics">${getSvgIcon('ethics', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.codeOfEthics')}</span></a></li>
              <li><a href="#manuals-and-datasheets" data-route="manuals-and-datasheets">${getSvgIcon('manuals', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.manuals') || 'Manuals & Datasheets'}</span></a></li>
            </ul>
            <ul class="sed-footer-links">
              <li><a href="#privacy-policy" data-route="privacy-policy">${getSvgIcon('privacy', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.privacyPolicy')}</span></a></li>
              <li><a href="#product-privacy" data-route="product-privacy">${getSvgIcon('productPrivacy', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.productPrivacy')}</span></a></li>
              <li><a href="#data-processing-agreement" data-route="data-processing-agreement">${getSvgIcon('dpa', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.dpa')}</span></a></li>
              <li><a href="#policy-ims" data-route="policy-ims">${getSvgIcon('ims', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.policyIMS')}</span></a></li>
              <li><a href="#cookie-policy" data-route="cookie-policy">${getSvgIcon('cookie', 14, 'rgba(255,255,255,0.85)')} <span>${t('nav.cookiePolicy')}</span></a></li>
              <li><a href="#cookie-settings" id="sed-footer-cookie-settings-btn" style="color: #35a8df; font-weight: 600; cursor: pointer;">${getSvgIcon('settings', 14, '#35a8df')} <span>${f.cookieSettings}</span></a></li>
            </ul>
          </div>
        </div>
      </div>

      <div class="sed-footer-bottom" style="margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid rgba(255,255,255,0.12); display: flex; flex-direction: column; gap: 10px; align-items: center; text-align: center;">
        <div style="font-size: 0.86rem; font-weight: 700; color: rgba(255,255,255,0.92); display: flex; align-items: center; justify-content: center; gap: 8px; flex-wrap: wrap;">
          <span>${t('footer.copyright')}</span>
          <span style="opacity: 0.5;">•</span>
          <span>${t('footer.trademark') || 'EU Registered Trademark Sound Event Detector® (EUIPO Reg. No. 018598475)'}</span>
        </div>
        <div style="font-size: 0.78rem; color: rgba(255,255,255,0.7); font-weight: 500;">
          ${t('footer.certifications') || 'ISO 9001 · ISO 27001 · CE Certified'} · ${f.madeIn}
        </div>
      </div>
    </div>
  `;

  // Attach internal routing click handlers
  footer.querySelectorAll('a[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const route = link.getAttribute('data-route');
      if (typeof onNavigate === 'function') {
        onNavigate(route);
      } else {
        window.location.hash = route;
      }
    });
  });

  // Attach Cookie Settings Recall handler
  const cookieSettingsBtn = footer.querySelector('#sed-footer-cookie-settings-btn');
  if (cookieSettingsBtn) {
    cookieSettingsBtn.addEventListener('click', (e) => {
      e.preventDefault();
      openCookieSettings(onNavigate);
    });
  }

  return footer;
}
